import {
  StoreIcon,
  SearchIcon,
  Building2Icon,
  CheckIcon,
  ClockIcon,
  XCircleIcon,
  AlertCircleIcon,
  Loader2Icon,
  UserPlusIcon,
} from 'lucide-react'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { toast } from 'sonner'

import { useDebounce } from '@nebula/shared/hooks'
import { associationsApiCustomer, type ResellerMarketplaceInfo } from '@nebula/shared/services'

const ResellersPage = () => {
  // State
  const [resellers, setResellers] = useState<ResellerMarketplaceInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedReseller, setSelectedReseller] = useState<ResellerMarketplaceInfo | null>(null)
  const [showRequestModal, setShowRequestModal] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 12

  // Load resellers on mount
  useEffect(() => {
    loadResellers()
  }, [currentPage])

  // ========================================
  // Data Loading
  // ========================================

  const loadResellers = async () => {
    try {
      setLoading(true)
      const response = await associationsApiCustomer.getAvailableResellers({
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      })

      setResellers(response.resellers)
      setTotalItems(response.total)
    } catch (error) {
      console.error('Error loading resellers:', error)
      toast.error('Erro ao carregar lista de resellers. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  // ========================================
  // Performance Optimization
  // ========================================

  // Debounce search term para evitar re-renders excessivos
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Memoizar resellers filtrados
  const filteredResellers = useMemo(() => {
    if (!debouncedSearchTerm) return resellers

    const lowerSearch = debouncedSearchTerm.toLowerCase()
    return resellers.filter(
      (reseller) =>
        reseller.name.toLowerCase().includes(lowerSearch) ||
        (reseller.description && reseller.description.toLowerCase().includes(lowerSearch)),
    )
  }, [resellers, debouncedSearchTerm])

  // ========================================
  // Actions (Memoizadas para estabilidade)
  // ========================================

  const handleRequestAssociation = useCallback((reseller: ResellerMarketplaceInfo) => {
    setSelectedReseller(reseller)
    setShowRequestModal(true)
  }, [])

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage)
  }, [])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  // ========================================
  // Render
  // ========================================

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <StoreIcon className="size-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Marketplace de Resellers</h1>
        </div>
        <p className="text-gray-600">
          Escolha um reseller para se associar e começar a comprar serviços
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
        <label htmlFor="search-resellers" className="sr-only">
          Buscar resellers
        </label>
        <div className="relative">
          <SearchIcon
            className="absolute top-1/2 left-3 size-5 -translate-y-1/2 transform text-gray-400"
            aria-hidden="true"
          />
          <input
            id="search-resellers"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar resellers por nome ou descrição..."
            aria-label="Buscar resellers por nome ou descrição"
            className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:ring-purple-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Loader2Icon className="size-8 animate-spin text-purple-600" aria-hidden="true" />
          <span className="sr-only">Carregando resellers...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredResellers.length === 0 && (
        <div className="rounded-lg bg-white p-12 text-center shadow">
          <StoreIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum reseller encontrado</h3>
          <p className="text-gray-500">
            {searchTerm
              ? 'Tente buscar com outros termos.'
              : 'Não há resellers disponíveis no momento.'}
          </p>
        </div>
      )}

      {/* Resellers Grid */}
      {!loading && filteredResellers.length > 0 && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredResellers.map((reseller) => (
            <ResellerCard
              key={reseller.id}
              reseller={reseller}
              onRequestAssociation={() => handleRequestAssociation(reseller)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalItems > itemsPerPage && (
        <div className="mt-6 flex items-center justify-between rounded-lg bg-white px-4 py-3 shadow">
          <div className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>{' '}
            até{' '}
            <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{' '}
            de <span className="font-medium">{totalItems}</span> resellers
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage * itemsPerPage >= totalItems}
              aria-label="Próxima página"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Request Modal */}
      {showRequestModal && selectedReseller && (
        <RequestAssociationModal
          reseller={selectedReseller}
          onClose={() => {
            setShowRequestModal(false)
            setSelectedReseller(null)
          }}
          onSuccess={() => {
            loadResellers() // Refresh list
          }}
        />
      )}
    </div>
  )
}

// ========================================
// Reseller Card Component
// ========================================

type ResellerCardProps = {
  reseller: ResellerMarketplaceInfo
  onRequestAssociation: () => void
}

const ResellerCard = ({ reseller, onRequestAssociation }: ResellerCardProps) => {
  const hasAssociation = reseller.has_association
  const status = reseller.association_status

  const getStatusBadge = () => {
    if (!hasAssociation) return null

    const configs = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: ClockIcon,
        label: 'Pendente',
      },
      active: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckIcon,
        label: 'Ativa',
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircleIcon,
        label: 'Rejeitada',
      },
      suspended: {
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: AlertCircleIcon,
        label: 'Suspensa',
      },
      cancelled: {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: XCircleIcon,
        label: 'Cancelada',
      },
    }

    const config = status ? configs[status as keyof typeof configs] : null
    if (!config) return null

    const Icon = config.icon

    return (
      <div
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${config.color}`}
      >
        <Icon className="size-3.5" />
        {config.label}
      </div>
    )
  }

  const canRequest = !hasAssociation || status === 'rejected' || status === 'cancelled'

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-purple-100">
              <Building2Icon className="size-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{reseller.name}</h3>
              {reseller.is_active ? (
                <span className="text-xs font-medium text-green-600">Ativo</span>
              ) : (
                <span className="text-xs text-gray-500">Inativo</span>
              )}
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Description */}
        {reseller.description && (
          <p className="mb-4 line-clamp-3 text-sm text-gray-600">{reseller.description}</p>
        )}

        {/* ID */}
        <div className="mb-4 text-xs text-gray-500">ID: {reseller.id.slice(0, 8)}...</div>

        {/* Action Button */}
        {canRequest && reseller.is_active && (
          <button
            onClick={onRequestAssociation}
            aria-label={`Solicitar associação com ${reseller.name}`}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-none"
          >
            <UserPlusIcon className="size-4" aria-hidden="true" />
            Solicitar Associação
          </button>
        )}

        {hasAssociation && status === 'active' && (
          <div
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-green-700"
            role="status"
            aria-label="Você já está associado a este reseller"
          >
            <CheckIcon className="size-4" aria-hidden="true" />
            Você já está associado
          </div>
        )}

        {hasAssociation && status === 'pending' && (
          <div
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-2 text-yellow-700"
            role="status"
            aria-label="Solicitação de associação aguardando aprovação"
          >
            <ClockIcon className="size-4" aria-hidden="true" />
            Aguardando aprovação
          </div>
        )}

        {hasAssociation && status === 'suspended' && (
          <div
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-orange-700"
            role="status"
            aria-label="Associação suspensa pelo reseller"
          >
            <AlertCircleIcon className="size-4" aria-hidden="true" />
            Associação suspensa
          </div>
        )}
      </div>
    </div>
  )
}

// ========================================
// Request Association Modal
// ========================================

type RequestAssociationModalProps = {
  reseller: ResellerMarketplaceInfo
  onClose: () => void
  onSuccess: () => void
}

const RequestAssociationModal = ({
  reseller,
  onClose,
  onSuccess,
}: RequestAssociationModalProps) => {
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)

      await associationsApiCustomer.requestAssociation(reseller.id)

      toast.success(`Solicitação enviada para ${reseller.name} com sucesso!`)
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error requesting association:', error)
      const errorMessage = error.response?.data?.detail || 'Erro ao solicitar associação'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 id="modal-title" className="text-lg font-semibold text-gray-900">
            Solicitar Associação
          </h3>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-6 py-4">
            {/* Reseller Info */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <div className="mb-2 flex items-center gap-3">
                <div
                  className="flex size-10 items-center justify-center rounded-full bg-purple-100"
                  aria-hidden="true"
                >
                  <Building2Icon className="size-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{reseller.name}</p>
                  {reseller.description && (
                    <p className="text-sm text-gray-600">{reseller.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Info Message */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" role="note">
              <p className="text-sm text-blue-800">
                Ao solicitar esta associação, você estará enviando uma solicitação que será avaliada
                pelo reseller. Você será notificado quando houver uma resposta.
              </p>
            </div>

            {/* Optional Message */}
            <div>
              <label
                htmlFor="association-message"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Mensagem (opcional)
              </label>
              <textarea
                id="association-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Adicione uma mensagem para o reseller..."
                rows={3}
                aria-describedby="message-help"
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
              <p id="message-help" className="mt-1 text-xs text-gray-500">
                Esta mensagem será enviada junto com sua solicitação
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-3"
                role="alert"
                aria-live="assertive"
              >
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Cancelar solicitação"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              aria-label={loading ? 'Enviando solicitação' : 'Confirmar solicitação de associação'}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
                  Solicitando...
                </>
              ) : (
                <>
                  <UserPlusIcon className="size-4" aria-hidden="true" />
                  Solicitar Associação
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ResellersPage
